/* @HEADER
 * @version 1.0.1919 | 2026-09-21
 * @purpose PersistenceSetup — the app-lifetime WS transport + persistence adapter (useOnlineMode over the JWT access token) and the current user's TeamMember row, plus RecordingContextBridge that tells the audio recorder which route is on screen; moved out of App.tsx in campaign 3 (ZVX-DEV-6084).
 * @sidecar PersistenceSetup.tsx.header.md
 * @layer app | @hex adapter | @ctx persistence
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-6084, ZVX-DEV-1312, ZVX-DEV-1349, ZVX-DEV-2655

import { useCallback, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useCurrentUserId, useCurrentUserRole } from '../auth';
import { useEnsureCurrentUserTeamMember } from '../features/admin/useEnsureCurrentUserTeamMember';
;
import { useOnlineMode } from '../hooks/useOnlineMode';
import { PersistenceContext } from '../hooks/PersistenceContext';
import { useRecordingContext } from '../shared/audio/RecordingContext';
import { readAccessToken } from '../auth/accessTokenStore.ts'

export function RecordingContextBridge({
  route,
  children,
}: {
  route: string;
  children: ReactNode;
}) {
  const { setRecordingContext } = useRecordingContext();
  useEffect(() => {
    setRecordingContext({ route });
  }, [route, setRecordingContext]);
  return <>{children}</>;
}

/**
 * PersistenceSetup — establishes the WS transport and persistence adapter for the
 * entire app lifetime. Rendered unconditionally inside AuthProvider so the WS
 * connection is open even before the user navigates into a board page.
 * ADR-0059 Wave 3.F / ZVX-DEV-1312.
 */
export function PersistenceSetup({ children }: { children: ReactNode }) {
  // ZVX-DEV-1349 — read the JWT access token directly from localStorage so
  // the WS connect URL stays in lockstep with the JwtAuthAdapter without
  // pulling the adapter into useOnlineMode's signature. `null` is returned
  // when no JWT is present so legacy sessions fall back to userId-based
  // tokens inside useOnlineMode.
  const getAccessToken = useCallback((): string | null => {
    try {
      if (typeof localStorage === 'undefined') return null;
      return readAccessToken();
    } catch {
      return null;
    }
  }, []);
  const persistenceState = useOnlineMode(useCurrentUserId, useCurrentUserRole, getAccessToken);
  // ZVX-DEV-2655 — ensure the current authenticated user always has a local
  // TeamMember row so they appear as an assignable candidate and can
  // self-assign cards. Mounted here (inside AuthProvider, app-lifetime) so
  // it fires once on boot/login rather than per-board-render.
  useEnsureCurrentUserTeamMember();
  return (
    <PersistenceContext.Provider value={persistenceState}>{children}</PersistenceContext.Provider>
  );
}
